import { Box, Typography, Button, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { EScanNetwork, IScan } from 'src/types'
import { formatDate, getScanExplorerTxUrl } from 'src/utils'
import { ExternalLinkIcon } from 'src/assets/icons/v2/ExternalLinkIcon'
import { BlockchainVerifiedModal } from '../blockchain-verified-banner/blockchain-modal'
import { useBlockchainVerifiedModal } from 'src/hooks'
import { COLORS } from 'src/styles/common'

interface IProps {
  scans: IScan[]
  primaryColor?: string
  walletAddress: string
  showWalletAddress: boolean
  accountImage?: string
  scanImage?: string
  scanName?: string
}

export const LatestBlockchainTransactions = ({
  scans,
  primaryColor,
  walletAddress,
  showWalletAddress,
  accountImage,
  scanImage,
  scanName,
}: IProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { currentIndex, handleClose, handleNext, handleOpen, handlePrev, open, setCurrentIndex } =
    useBlockchainVerifiedModal()

  // Sort scans by created_at (newest first) and take latest 5
  const sortedScans = [...scans]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5)

  // Create a map of scan IDs to their index in the full sorted scans array
  const allSortedScans = [...scans].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
  const scanIndexMap = new Map(allSortedScans.map((scan, index) => [scan.id, index]))

  const hasMoreThanFive = scans.length > 5

  const truncateTxHash = (hash: string) => {
    if (isMobile) {
      return `${hash.slice(0, 20)}...${hash.slice(-6)}`
    }
    return hash
  }

  const handleTransactionClick = (scanId: string) => {
    const index = scanIndexMap.get(scanId) ?? 0
    handleOpen(index)
  }

  if (sortedScans.length === 0) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          mt: '15px',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            mb: '15px',
            color: theme.palette.common.black,
          }}
        >
          Latest Blockchain Transactions
        </Typography>

        {sortedScans.map((scan) => (
          <Box key={scan.id}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                pb: '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  color: theme.palette.common.black,
                  fontWeight: 400,
                }}
              >
                {formatDate(scan.created_at)}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  maxWidth: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: COLORS.GREY_LIGHT,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => handleTransactionClick(scan.id)}
                >
                  {truncateTxHash(scan.account_tx)}
                </Typography>

                <IconButton
                  component="a"
                  href={getScanExplorerTxUrl(scan.account_tx, scan.network || EScanNetwork.BASE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    p: '1px',
                    minWidth: '20px',
                    width: '20px',
                    height: 'auto',
                  }}
                >
                  <ExternalLinkIcon size={18} color={COLORS.GREY_LIGHT} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}

        {hasMoreThanFive && (
          <Box sx={{ mt: '10px' }}>
            <Button
              variant="outlined"
              onClick={() => handleOpen(0)}
              size="small"
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                borderColor: primaryColor || theme.palette.primary.main,
                color: primaryColor || theme.palette.primary.main,
              }}
            >
              View All Transactions
            </Button>
          </Box>
        )}
      </Box>

      <BlockchainVerifiedModal
        currentIndex={currentIndex}
        handleClose={handleClose}
        handleNext={handleNext}
        handlePrev={handlePrev}
        open={open}
        scans={allSortedScans}
        setCurrentIndex={setCurrentIndex}
        primaryColor={primaryColor}
        walletAddress={walletAddress}
        showWalletAddress={showWalletAddress}
        accountImage={accountImage}
        scanImage={scanImage}
        scanName={scanName}
      />
    </>
  )
}
